import { useCallback, useState } from 'react';

import { Button } from '@/components/ui/button';
import { TextInput } from '@/components/ui/form';
import http from '@/lib/http';
import { urlUploadCmsAssetsPath } from '@/lib/routes';
import { isValidUrl } from '../utils';

interface UrlImportSectionProps {
  onUploadComplete: () => void;
  currentFolderId?: string | null;
  /** Called when the uploading state changes — lets the parent block close during import. */
  onUploadingStateChange?: (isUploading: boolean) => void;
}

export function UrlImportSection({
  onUploadComplete,
  currentFolderId,
  onUploadingStateChange,
}: UrlImportSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  const handleUpload = useCallback(async () => {
    if (!isValidUrl(urlInput)) {
      setUrlError('Please enter a valid URL');
      return;
    }

    setIsUploading(true);
    onUploadingStateChange?.(true);
    setUrlError(null);

    try {
      await http.post(urlUploadCmsAssetsPath(), {
        url: urlInput,
        ...(currentFolderId ? { folderId: currentFolderId } : {}),
      });
      setUrlInput('');
      setIsExpanded(false);
      onUploadComplete();
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error.response as { data?: { message?: string } })?.data?.message
          : undefined;
      setUrlError(
        errorMessage ?? 'Failed to upload file from URL. Please check the URL and try again.',
      );
    } finally {
      setIsUploading(false);
      onUploadingStateChange?.(false);
    }
  }, [urlInput, currentFolderId, onUploadComplete, onUploadingStateChange]);

  return (
    <div className="flex flex-col items-center gap-3">
      <Button
        variant="link"
        className="font-semibold hover:no-underline hover:opacity-80"
        onClick={() => {
          setIsExpanded((prev) => !prev);
          setUrlError(null);
        }}
      >
        Import a file from a URL
      </Button>

      {isExpanded && (
        <div className="flex w-full max-w-2xl gap-2">
          <TextInput
            value={urlInput}
            onChange={(e) => {
              setUrlInput(e.target.value);
              setUrlError(null);
            }}
            placeholder="Paste URL here"
            disabled={isUploading}
            invalid={!!urlError}
            errorText={urlError || undefined}
            className="bg-background"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isUploading && urlInput.trim()) {
                void handleUpload();
              }
            }}
          />
          <Button
            onClick={() => void handleUpload()}
            disabled={isUploading || !urlInput.trim()}
            isLoading={isUploading}
            loadingText="Importing..."
            loaderPlacement="start"
            className="shrink-0"
          >
            Import
          </Button>
        </div>
      )}
    </div>
  );
}
